import { Link, useNavigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import CtaBand from '@/components/CtaBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { features } from '@/content/features'
import { businessTypes } from '@/content/businessTypes'
import { homePage } from '@/content/home'
import { cardImageOf, heroImageOf } from '@/content/applyImages'

function LandingPage() {
  const navigate = useNavigate()

  const pillars = homePage.pillars
  const highlightFeatures = features.slice(0, 6)
  const heroSrc = heroImageOf(homePage)

  return (
    <SiteLayout>
      <Seo
        title={homePage.seoTitle}
        description={homePage.seoDescription}
        path={homePage.path}
        image={heroSrc}
      />

      <PageHero
        title={homePage.hero.title}
        description={homePage.hero.description}
        image={heroSrc}
        imageAlt="Restaurant POS System"
        actions={
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Button size="lg" onClick={() => navigate('/onboarding')}>
              Get Started
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/features')}>
              Explore Features
            </Button>
          </div>
        }
      />

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Choose what you need
          </h2>
          <p className="text-xl text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            Start with the capabilities that matter most—then grow into the full platform.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((category) => (
              <Link key={category.title} to={category.to} className="block group">
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle>{category.title}</CardTitle>
                    <CardDescription>{category.text}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            The only software you&apos;ll ever need.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-red-50 border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">Before Tillio POS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Restaurants juggle multiple systems for POS, scheduling, payroll, and inventory—plus constant switching between apps.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-700">After Tillio POS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  One intuitive platform for POS, orders, team management, time tracking, menus, and analytics.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Everything in a modern POS system
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Point of sale, tables, payments, team tools, and analytics—built for restaurant service.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/features')}>
              View all features
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {highlightFeatures.map((feature) => (
              <Link key={feature.slug} to={feature.path} className="block group">
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={cardImageOf(feature)}
                      alt={feature.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription>{feature.hubDescription}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Tools for every type of business
          </h2>
          <p className="text-xl text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            From coffee shops to fine dining, Tillio POS helps you take payments, run service, and grow.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {businessTypes.map((type) => (
              <Link key={type.slug} to={type.path} className="block group">
                <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={cardImageOf(type)}
                      alt={type.cardTitle}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader className="text-center">
                    <CardTitle className="text-lg">{type.cardTitle}</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-8">
            A quick memo from a happy customer.
          </h2>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-2xl italic mb-6 leading-relaxed">
              &quot;Going from multiple systems to Tillio POS has been a great decision for our business. Everything we need is in one place.&quot;
            </p>
            <p className="text-xl font-semibold mb-2">Restaurant Owner</p>
            <p className="text-lg opacity-90">3 locations</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background text-center">
        <div className="container mx-auto max-w-3xl px-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Have questions?</h2>
          <p className="text-lg text-muted-foreground mb-6">
            See pricing, hardware options, or browse our FAQ.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/pricing')}>Pricing</Button>
            <Button variant="outline" onClick={() => navigate('/hardware')}>Hardware</Button>
            <Button variant="outline" onClick={() => navigate('/faq')}>FAQ</Button>
          </div>
        </div>
      </section>

      <CtaBand />
    </SiteLayout>
  )
}

export default LandingPage
